/**
 * Analoge Uhr mit Anrufzeitfenster – die letzte Karte der rechten Spalte.
 *
 * Warum analog: Man schätzt „noch eine gute Stunde" am Zeigerwinkel schneller
 * ab als an einer Ziffer – und genau diese Frage steht vor einem Kundenanruf
 * im Kopf. Die digitale Zeit steht trotzdem daneben, für alles andere.
 *
 * Warum als Karte im Fluss und nicht schwebend unten rechts: Dieser Platz
 * gehört Kora. Ein zweites festes Element dort verdeckte in langen Listen die
 * Aktions-Icons der Zeilen, und zwei fixe Ecken überlagern sich früher oder
 * später gegenseitig.
 *
 * Die Zeitgrenzen kommen aus den Einstellungen, nicht aus dem Code: Bauservice
 * ruft anders an als Innentüren.
 */

import { useEffect, useState } from 'react';

import type { Anrufzeit } from '../typen';
import './anrufuhr.css';

const WOCHENTAGE = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
];
const MONATE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

type Lage = 'gut' | 'warten' | 'vorbei';

interface Fenster {
  lage: Lage;
  text: string;
}

/**
 * Welcher Zustand gerade gilt.
 *
 * Getrennt von der Darstellung, damit die Regel an einer Stelle steht und sich
 * ohne die Uhr lesen lässt.
 */
export function anrufFenster(jetzt: Date, z: Anrufzeit): Fenster {
  const uhr = (h: number) => `${String(h).padStart(2, '0')}:00`;
  const tag = jetzt.getDay();
  if (tag === 0 || tag === 6) return { lage: 'vorbei', text: 'Wochenende · außerhalb der Anrufzeit' };

  const stunde = jetzt.getHours();
  if (stunde < z.von || stunde >= z.bis) return { lage: 'vorbei', text: 'Außerhalb der Anrufzeit' };
  if (stunde >= z.mittagAb && stunde < z.mittagBis)
    return { lage: 'warten', text: `Mittag – Anrufe lieber ab ${uhr(z.mittagBis)}` };
  if (stunde >= z.letzteAb) return { lage: 'warten', text: 'Letzte Stunde für Anrufe' };

  /*
   * Die gute Zeit nennt ihre Grenze (v13, 11.2). „Gute Zeit für Anrufe"
   * allein sagte nicht, wie lange noch – und wer um 11:55 zum Hörer greift,
   * erwischt den Kunden in der Mittagspause.
   */
  const grenze = stunde < z.mittagAb ? z.mittagAb : z.letzteAb;
  return { lage: 'gut', text: `Gute Anrufzeit · bis ${uhr(grenze)}` };
}

export function Anrufuhr({ zeit }: { zeit: Anrufzeit }) {
  /*
   * Alle zwanzig Sekunden. Sekundengenau bräuchte einen Sekundenzeiger, den
   * die Uhr nicht hat – und ein Zeiger, der ruckt, zieht den Blick auf sich,
   * ohne etwas zu sagen.
   */
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setJetzt(new Date()), 20_000);
    return () => clearInterval(t);
  }, []);

  const fenster = anrufFenster(jetzt, zeit);
  const stunde = jetzt.getHours();
  const minute = jetzt.getMinutes();

  /* Der Stundenzeiger wandert mit der Minute weiter – sonst springt er. */
  const stundenWinkel = ((stunde % 12) + minute / 60) * 30;
  const minutenWinkel = minute * 6;

  const digital = `${String(stunde).padStart(2, '0')}:${String(minute).padStart(2, '0')} Uhr`;
  const datum = `${WOCHENTAGE[jetzt.getDay()]}, ${jetzt.getDate()}. ${MONATE[jetzt.getMonth()]}`;

  return (
    <div className="mt-karte uhr">
      {/*
        Das Datum steht über der Uhr, nicht daneben (v13, 5.1).
        Neben dem 68-px-Zifferblatt bleiben in der 320-px-Spalte nur rund
        196 px, und „Donnerstag, 24. September" braucht 215 – es brach in
        zwei Zeilen um und schob den Anrufzeit-Hinweis nach unten. Über der
        Uhr hat es die ganze Kartenbreite.
      */}
      <div className="uhr-datum">{datum}</div>

      <div className="uhr-reihe">
      <div className={`uhr-blatt ${fenster.lage}`} aria-hidden="true">
        <svg viewBox="0 0 68 68" width={68} height={68}>
          <circle cx="34" cy="34" r="33" className="uhr-rand" />
          {/* Vier Striche statt zwölf: Die Uhr soll ablesbar sein, nicht
              genau – für „noch eine gute Stunde" reichen die Viertel. */}
          {[0, 90, 180, 270].map((grad) => (
            <line
              key={grad}
              x1="34"
              y1="6"
              x2="34"
              y2="11"
              className="uhr-strich"
              transform={`rotate(${grad} 34 34)`}
            />
          ))}
          <line
            x1="34"
            y1="34"
            x2="34"
            y2="17"
            className="uhr-stunde"
            transform={`rotate(${stundenWinkel} 34 34)`}
          />
          <line
            x1="34"
            y1="34"
            x2="34"
            y2="10"
            className="uhr-minute"
            transform={`rotate(${minutenWinkel} 34 34)`}
          />
          <circle cx="34" cy="34" r="2.5" className="uhr-mitte" />
        </svg>
      </div>

      <div className="uhr-text">
        <div className="uhr-zeit">{digital}</div>
        <div className={`uhr-fenster ${fenster.lage}`}>
          <span className="uhr-punkt" />
          {fenster.text}
        </div>
      </div>
      </div>
    </div>
  );
}
